import React from "react"
import autobind from "class-autobind"
import { Drawer, Paper } from "material-ui"
import { InfoWindow, GoogleMapLoader, GoogleMap, Marker } from "react-google-maps"
import { triggerEvent } from "react-google-maps/lib/utils"
import { default as MarkerClusterer } from "react-google-maps/lib/addons/MarkerClusterer"
import ImageLoader from "../../components/misc/ImageLoader"
import strings from "../../localization/strings"
import AccountStore from "../../stores/AccountStore"
import * as mappingHelper from "../../helpers/mappingHelper"
import { IMapMarker, IPunchMapData } from "../../models"
import * as timeHelper from "../../helpers/timeHelper"
import moment from "moment"
import JSONbig from 'json-bigint'
import AppStateStore from "../../stores/AppStateStore"

interface IProps {
    records: IPunchMapData[]
}
interface IState {
    markers: IMapMarker[]
    consolidateMarkers: boolean
    drawerOpen: boolean
    drawerMarkers: IMapMarker[]
}

export default class DashboardMap extends React.Component<IProps, IState> {
    private setBounds
    private firstDraw
    private _googleMapComponent
    constructor(props: IProps) {
        super(props)
        this.state = {
            markers: [],
            consolidateMarkers: false,
            drawerOpen: false,
            drawerMarkers: null
        }
        this.setBounds = true
        this.firstDraw = true
        autobind(this)
    }

    componentDidMount() {
        setTimeout(() => this._googleMapComponent && this.drawMap(), 0)
        this.updateMarkers(this.props.records)
    }

    componentWillReceiveProps(nextProps) {
        this.updateMarkers(nextProps.records)
    }

    offsetMarkerPosition(markers) {
        const OFFSET_DISTANCE = 0.00002 // Small offset distance in degrees

        const updatedMarkers = markers.map((marker, index) => {
            let identicalMarkers = markers.filter(m =>
                m.position.lat === marker.position.lat && m.position.lng === marker.position.lng
            )

            if (identicalMarkers.length > 1) {
                // Offset only if there are other markers at the same position
                const angle = (index / identicalMarkers.length) * 360
                const offsetLat = marker.position.lat + OFFSET_DISTANCE * Math.cos(angle)
                const offsetLng = marker.position.lng + OFFSET_DISTANCE * Math.sin(angle)

                return {
                    ...marker,
                    position: { lat: offsetLat, lng: offsetLng }
                }
            }

            return marker
        })

        return updatedMarkers
    }

    updateMarkers(records) {
        const markers = this.getMapMarkers(records)
        this.setState({ markers: markers })
    }

    drawMap() {
        const defaultLat = AccountStore.getCurrentAccount().AccountSettings.LocationSettings.DefaultMapLatitude
        const defaultLng = AccountStore.getCurrentAccount().AccountSettings.LocationSettings.DefaultMapLongitude
        if (!this.state.markers || this.state.markers.length === 0) {
            if (this.firstDraw) {
                this.firstDraw = false
            } else {
                return
            }
            const bounds = new google.maps.LatLngBounds()

            bounds.extend(new google.maps.LatLng(defaultLat, defaultLng))
            //we can't setZoom with this google map API. Create other points arround the center which will auto-zoom the map.
            bounds.extend(new google.maps.LatLng(defaultLat - 10, defaultLng - 10))
            bounds.extend(new google.maps.LatLng(defaultLat + 10, defaultLng - 10))
            bounds.extend(new google.maps.LatLng(defaultLat - 10, defaultLng + 10))
            bounds.extend(new google.maps.LatLng(defaultLat + 10, defaultLng + 10))
            this._googleMapComponent.fitBounds(bounds)
        }
    }

    getMapMarkers(records: IPunchMapData[]): IMapMarker[] {
        if (!records || records.length === 0) return []

        const markers: IMapMarker[] = []
        const bounds = new google.maps.LatLngBounds()

        if (this.state.consolidateMarkers) {
            return mappingHelper.consolidateMarkers(records)
        }

        if (records) {
            for (const record of records) {
                let hasGPSFromPunch = true

                let lat = typeof record.Latitude === "object" ? Number(JSONbig.stringify(record.Latitude)) : record.Latitude
                let lng = typeof record.Longitude === "object" ? Number(JSONbig.stringify(record.Longitude)) : record.Longitude

                if (lat !== 0 && lng !== 0) {
                    const punchMarker = {
                        position: { lat, lng },
                        record: record,
                        showInfoWindow: false,
                        key: record.Id,
                        icon: hasGPSFromPunch ? "/assets/markers/punch-pin.png" : "/assets/markers/punch-pin-location.png"
                    } as IMapMarker
                    markers.push(punchMarker)
                    bounds.extend(new google.maps.LatLng(lat, lng))
                }
            }
        }

        if (this.setBounds) {
            if (this._googleMapComponent) {
                triggerEvent(this._googleMapComponent, "resize")
                if (markers && markers.length > 0) {
                    this._googleMapComponent.fitBounds(bounds)
                    this.setBounds = false
                }
            }
        }
        return this.offsetMarkerPosition(markers)
    }

    handleMarkerMouseOver(index) {
        this.state.markers[index].showInfoWindow = true
        this.setState({ markers: this.state.markers })
    }

    handleMarkerClick(index) {
        if (this.state.markers[index].stacked) {
            const drawerMarkers = Array()
            drawerMarkers.push(this.state.markers[index])

            for (const childMarker of this.state.markers[index].childMarkers) {
                drawerMarkers.push(childMarker)
            }

            this.setState({
                drawerOpen: true,
                drawerMarkers: drawerMarkers
            })
        }
    }

    handleMarkerClose(index) {
        this.state.markers[index].showInfoWindow = false
        this.setState({ markers: this.state.markers })
    }

    handleMapZoomChange() {
        // if (this._googleMapComponent.getZoom() >= 15) {
        //     this.setState(
        //         {
        //             consolidateMarkers: true
        //         },
        //         () => {
        //             this.updateMarkers()
        //         }
        //     )
        // } else {
        //     this.setState(
        //         {
        //             consolidateMarkers: false
        //         },
        //         () => {
        //             this.updateMarkers()
        //         }
        //     )
        // }
    }

    renderInfoWindow(marker: IMapMarker, index) {
        return (
            <InfoWindow onCloseclick={() => this.handleMarkerClose(index)}>
                {marker.stacked ?
                    <div>
                        <h4>
                            <i className="fa fa-hand-pointer-o" /> {strings.formatString(strings.click_to_view_x_records, marker.recordCount)}
                        </h4>
                    </div> :
                    marker.record &&
                    <div>
                        <h4>
                            {marker.record.UserName}
                        </h4>
                        <div>
                            {moment(timeHelper.keepConvertUTCToLocal(marker.record.PunchTimeLocal)).format("h:mm A  MMM. DD, YYYY")}
                        </div>
                        {marker.record.ImageID ?
                            <ImageLoader url={marker.record.ImageS3Url} fixedHeight={150} src={"/punches/image/" + marker.record.ImageID} /> :
                            <div style={{ textAlign: "center", marginTop: 20, marginBottom: 10, marginLeft: 20 }}>
                                {strings.Punch_added_without_image}
                            </div>
                        }
                    </div>
                }
            </InfoWindow>
        )
    }

    render() {
        return (
            <div
                style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0 }}
            >
                <GoogleMapLoader
                    containerElement={<div className="google-map-loader-custom" style={{ height: "100%", width: "100%" }} />}
                    googleMapElement={
                        <GoogleMap ref={map => this._googleMapComponent = map} defaultZoom={4} onZoomChanged={this.handleMapZoomChange}>
                            {this.state.markers && (
                                !this.state.consolidateMarkers ?
                                    <MarkerClusterer
                                        averageCenter
                                        gridSize={50}
                                    >
                                        {this.state.markers.map((marker, index) =>
                                            <Marker {...marker} onMouseout={() => this.handleMarkerClose(index)} onMouseover={() => this.handleMarkerMouseOver(index)}>
                                                {marker.showInfoWindow ? this.renderInfoWindow(marker, index) : null}
                                            </Marker>
                                        )}
                                    </MarkerClusterer> :
                                    this.state.markers.map((marker, index) =>
                                        <Marker
                                            {...marker}
                                            onClick={() => this.handleMarkerClick(index)}
                                            onMouseover={() => this.handleMarkerMouseOver(index)}
                                            onMouseout={() => this.handleMarkerClose(index)}
                                        >
                                            {marker.showInfoWindow ? this.renderInfoWindow(marker, index) : null}
                                        </Marker>
                                    )
                            )}
                        </GoogleMap>
                    }
                />
                <Drawer
                    openSecondary
                    docked={false}
                    open={this.state.drawerOpen}
                    onRequestChange={() => this.setState({ drawerOpen: false })}
                >
                    <div>
                        <div style={{ padding: 15, backgroundColor: AppStateStore.getFrontEndSettings().UISettings.AccentColor, height: 70 }}>
                            <h2 style={{ color: "#ffffff" }}>
                                {strings.formatString(strings.x_Records, this.state.drawerMarkers ? this.state.drawerMarkers.length : "")}
                                <i className="fa fa-close itemActionButton" style={{ float: "right" }} onClick={() => this.setState({ drawerOpen: false })} />
                            </h2>
                        </div>
                        {this.state.drawerMarkers &&
                            this.state.drawerMarkers.map((marker, index) =>
                                <Paper
                                    key={marker.record.Id}
                                    style={{ padding: 10, margin: 10 }}
                                >
                                    <h4 style={{ margin: 0 }}>
                                        {marker.record.UserName}
                                    </h4>
                                    <div>
                                        {moment(timeHelper.keepConvertUTCToLocal(marker.record.PunchTimeLocal)).format("h:mm A  MMM. DD, YYYY")}
                                    </div>
                                    {/* <div>
                                        {marker.record.LocationName} <b>{marker.record.Latitude === 0 && marker.record.Longitude === 0 ? " - NO GPS" : ""}</b>
                                    </div> */}
                                    {marker.record.ImageID ?
                                        <ImageLoader url={marker.record.ImageS3Url} fixedHeight={150} src={"/punches/image/" + marker.record.ImageID} /> :
                                        <div style={{ textAlign: "center", marginTop: 20, marginBottom: 10 }}>
                                            {strings.Punch_added_without_image}
                                        </div>
                                    }
                                </Paper>
                            )
                        }
                    </div>
                </Drawer>
            </div>
        )
    }
}
